import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, computed, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiButtonComponent, UiNoDataComponent } from '@ui';
import { take } from 'rxjs';
import {
  AlertCategory,
  CustomizedCapabilitiesType,
  FlowLookup,
  SafetyType,
  SystemType,
} from '@models/alerts-v2.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { PermissionModel } from '@models/permission.model';
import { AlertV2TypeGroup } from '@consts/alerts-v2.const';
import { CameraEditActions } from '@states/camera-edit/camera-edit.action-types';
import { CameraEditSelectors } from '@states/camera-edit/camera-edit.selector-types';
import { AccessDoorsActions } from '@states/access-doors/access-doors.action-types';
import { AccessDoorsSelectors } from '@states/access-doors/access-doors.selector-types';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { CustomObjectStatesSelectors } from '@states/custom-objects-states/custom-objects-states.selector-types';
import { DoorsActions } from '@states/doors/doors.action-types';
import { DoorsSelectors } from '@states/doors/doors.selector-types';
import { LocationActions } from '@states/location/location.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { PermissionSelectors } from '@states/permissions/permissions.selector-types';
import { ShelfsActions } from '@states/shelfs/shelfs.action-types';
import { ShelfsSelectors } from '@states/shelfs/shelfs.selector-types';
import {
  StepOptionSelectorGroup,
  StepOptionSelectorGroupEntry,
} from '../../../../../../pages/alerts-v2/components/step-option-selector/step-option-selector.component';
import {
  EventSentenceTemplateService,
  FlowTypes,
} from '../../../../../services/event-sentence-template.service';
import {
  filterAlertV2TypeGroupsByAvailability,
  isAlertV2TemplateDisabled,
} from '../alert-type-filter.util';
import { PipesModule } from '../../../../../../pipes/pipes.module';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { PanicSystemConfigurationService } from '../../../../../../development/panic-system-configuration.service';

const CATEGORY_ICONS: Partial<Record<AlertCategory, string>> = {
  [AlertCategory.Safety]: 'lock',
  [AlertCategory.Identification]: 'cat-identification',
  [AlertCategory.Tracking]: 'cat-tracking',
  [AlertCategory.Status]: 'cat-status',
  [AlertCategory.CustomizedCapabilities]: 'safety',
  [AlertCategory.Integrations]: 'integrations',
  [AlertCategory.Retail]: 'shelf-cat',
  [AlertCategory.ObjectStates]: 'object-state-change',
  [AlertCategory.Monitoring]: 'object-edge',
};

@Component({
  selector: 'app-alert-templates-result',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltip,
    PipesModule,
    TranslatePipe,
    UiButtonComponent,
    UiNoDataComponent,
    UiSearchComponent,
  ],
  templateUrl: './alert-templates-result.component.html',
  styleUrls: ['./alert-templates-result.component.scss'],
})
export class AlertTemplatesResultComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly sentenceTemplateService = inject(EventSentenceTemplateService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly intelligenceResultsService = inject(IntelligenceResultsService);
  private readonly panicSystemConfigurationService = inject(PanicSystemConfigurationService);

  protected readonly searchQuery = signal<string>('');

  private readonly hasProtectiveGear = this.store$.selectSignal(
    CameraEditSelectors.selectOrgProtectiveGear,
  );
  private readonly savedDoors = this.store$.selectSignal(DoorsSelectors.selectSavedDoors);
  private readonly hasShelves = this.store$.selectSignal(ShelfsSelectors.hasShelves);
  private readonly isDeveloper = this.store$.selectSignal(OrganizationSelectors.isDeveloper);
  private readonly permissions = this.store$.selectSignal(PermissionSelectors.selectPermissions);
  private readonly hasAccessDoors = this.store$.selectSignal(
    AccessDoorsSelectors.selectHasAccessDoors,
  );
  private readonly activeOrganization = this.store$.selectSignal(
    OrganizationSelectors.selectActiveOrganization,
  );
  private readonly shelvesEnabled = this.store$.selectSignal(
    OrganizationSelectors.selectEnableShelves,
  );
  private readonly objectStates = this.store$.selectSignal(
    CustomObjectStatesSelectors.selectAllCustomObjectStates,
  );
  private readonly physicalAccessControls = this.store$.selectSignal(
    LocationSelectors.selectPhysicalAccessControls,
  );
  private readonly faceRecognitionEnabled = this.store$.selectSignal(
    LocationSelectors.isFaceRecognitionEnabled,
  );
  private readonly enablePalestinianLicensePlate = this.store$.selectSignal(
    OrganizationSelectors.selectEnablePalestinianLicensePlate,
  );
  private readonly disableMissingObjectEventConfiguration = this.store$.selectSignal(
    OrganizationSelectors.selectDisableMissingObjectEventConfiguration,
  );

  private readonly templateAvailability =
    computed<IntelligenceModels.AlertConfigurations.TemplateAvailability>(
      () => ({
        hasProtectiveGear: !!this.hasProtectiveGear(),
        hasDoors: !!this.savedDoors()?.length,
        hasShelves: !!this.hasShelves(),
        isDeveloper: !!this.isDeveloper(),
        permissions: this.permissions() as PermissionModel.UserPermissionsState,
        hasAccessDoors: !!this.hasAccessDoors(),
        violenceEnabled: !!this.activeOrganization()?.violenceEventEnabled,
        shelvesEnabled: !!this.shelvesEnabled(),
        hasObjectStates: !!this.objectStates()?.length,
        hasPhysicalAccessControls: !!this.physicalAccessControls()?.length,
        faceRecognitionEnabled: !!this.faceRecognitionEnabled(),
        enablePalestinianLicensePlate:
          !!this.enablePalestinianLicensePlate(),
        disableMissingObjectEventConfiguration:
          !!this.disableMissingObjectEventConfiguration(),
      }),
    );

  protected readonly availableGroups = computed<StepOptionSelectorGroup[]>(() =>
    filterAlertV2TypeGroupsByAvailability(
      AlertV2TypeGroup,
      this.templateAvailability(),
    ),
  );

  protected readonly filteredGroups = computed<StepOptionSelectorGroup[]>(() => {
    const search = this.searchQuery().trim().toLocaleLowerCase();
    if (!search) return this.availableGroups();

    return this.availableGroups()
      .map((group) => ({
        ...group,
        options: group.options.filter((option) =>
          this.translateService.instant(option.name).toLocaleLowerCase().includes(search),
        ),
      }))
      .filter((group) => group.options.length > 0);
  });

  protected readonly totalCount = computed<number>(() =>
    this.filteredGroups().reduce((total, group) => total + group.options.length, 0),
  );

  public ngOnInit(): void {
    this.store$.dispatch(CameraEditActions.getHasProtectiveGear());
    this.store$.dispatch(CameraEditActions.getHasOrgContainer());
    this.store$.dispatch(CameraEditActions.getHasShoppingCart());
    this.store$.dispatch(AccessDoorsActions.getOrganizationAccessDoors({}));
    this.store$.dispatch(DoorsActions.getDoors());
    this.store$.dispatch(LocationActions.getPhysicalAccessControl());
    this.store$.dispatch(CustomObjectsActions.getSavedCustomObjectStates());
    this.store$.dispatch(ShelfsActions.getShelfs());
  }

  protected onSearchInput(value: string): void {
    this.searchQuery.set(value ?? '');
  }

  protected scrollToCategory(category: AlertCategory): void {
    const section = this.elementRef.nativeElement.querySelector<HTMLElement>(
      `[data-alert-template-category="${category}"]`,
    );
    section?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  protected categoryForGroup(group: StepOptionSelectorGroup): AlertCategory {
    return +group.options[0].value.category as AlertCategory;
  }

  protected categoryIcon(category: AlertCategory): string {
    return CATEGORY_ICONS[category] ?? 'lock';
  }

  protected templateSentence(option: StepOptionSelectorGroupEntry): string {
    return this.sentenceTemplateService.generateTemplateSentence(
      +option.value.category as AlertCategory,
      +option.value.type as FlowTypes,
    );
  }

  protected isTemplateDisabled(option: StepOptionSelectorGroupEntry): boolean {
    return isAlertV2TemplateDisabled(
      option,
      this.templateAvailability(),
    );
  }

  protected disabledTooltip(option: StepOptionSelectorGroupEntry): string {
    const category = +option.value.category as AlertCategory;
    const type = +option.value.type;

    if (
      category === AlertCategory.CustomizedCapabilities &&
      type === CustomizedCapabilitiesType.ProtectiveGear &&
      !this.hasProtectiveGear()
    ) {
      return 'ALERT_TEMPLATES.NO_SAFETY_GEAR_TOOLTIP';
    }
    if (
      category === AlertCategory.Safety &&
      type === SafetyType.Violence &&
      !this.activeOrganization()?.violenceEventEnabled
    ) {
      return 'ALERT_TEMPLATES.OPTION_TOOLTIP';
    }
    return '';
  }

  /** Open the in-panel alert creation flow prefilled with the chosen template. */
  protected useTemplate(option: StepOptionSelectorGroupEntry): void {
    if (this.isTemplateDisabled(option)) return;
    const template: FlowLookup = {
      category: +option.value.category as AlertCategory,
      flowType: +option.value.type,
    } as FlowLookup;
    const currentResult = this.intelligenceResultsService.current();
    const currentDraft =
      currentResult?.type === 'alert-templates'
        ? (currentResult.data as IntelligenceModels.AlertConfigurations.CreateAlertDraft)
        : null;
    const label = this.translateService.instant(option.name);

    if (template.category === AlertCategory.System && template.flowType === SystemType.Panic) {
      this.panicSystemConfigurationService
        .getPanicEvent()
        .pipe(take(1))
        .subscribe({
          next: (panicEvent) => {
            this.intelligenceResultsService.setOverlay(
              { template, name: currentDraft?.name, configuration: panicEvent ?? undefined },
              'create-alert',
              label,
            );
          },
          error: () => {
            this.intelligenceResultsService.setOverlay(
              { template, name: currentDraft?.name },
              'create-alert',
              label,
            );
          },
        });
      return;
    }

    const draft: IntelligenceModels.AlertConfigurations.CreateAlertDraft = {
      template,
      name: currentDraft?.name,
    };
    this.intelligenceResultsService.setOverlay(draft, 'create-alert', label);
  }

  protected trackByCategory(_index: number, group: StepOptionSelectorGroup): number {
    return +group.options[0].value.category;
  }

  protected trackByTemplate(_index: number, option: StepOptionSelectorGroupEntry): string {
    return `${+option.value.category}:${+option.value.type}`;
  }
}
